import React, { useState, useRef, useCallback } from "react";
import {
  Box,
  Stack,
  Button,
  Text,
  Icon,
  Divider,
  IconButton,
  HStack,
  Spinner,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  keyframes,
} from "@chakra-ui/react";
import { FiTrash2, FiEdit2, FiMoreVertical } from "react-icons/fi";
import { DarwinHeader } from "build-ts/common";
import EditChatModal from "components/modals/EditChatModal";
import { TbLayoutSidebarLeftExpandFilled, TbLayoutSidebarRightExpandFilled } from "react-icons/tb";
import { IoChatbubbleOutline } from "react-icons/io5";


// Speaking wave animations - arcs expanding outward
const speakWave1 = keyframes`
  0% {
    transform: scale(0.9);
    opacity: 0;
  }
  20% {
    opacity: 0.7;
  }
  50% {
    transform: scale(1.6);
    opacity: 0;
  }
  100% {
    transform: scale(1.6);
    opacity: 0;
  }
`;

// Speaking Icon Wrapper Component
const SpeakingIconWrapper: React.FC<{ icon: React.ComponentType; boxSize: number | string }> = ({ icon, boxSize }) => {
  return (
    <Box position="relative" display="inline-block" width={boxSize} height={boxSize} flexShrink={0}>
      <Icon as={icon} boxSize={boxSize} color="fg.default" zIndex={2} position="relative" />

      {/* Sound wave arcs emanating from bottom - centered on icon */}
      {/* Arc 1 - Black */}
      <Box
        position="absolute"
        bottom="2px"
        left="0%"
        width="33px"
        height="21px"
        borderRadius="100%"
        border="2.5px solid"
        borderColor="purple.700"
        borderTop="transparent"
        borderLeft="transparent"
        borderRight="transparent"
        transform="translateX(-50%) rotate(-30deg)"
        transformOrigin="center top"
        clipPath="polygon(0% 85%, 100% 85%, 100% 100%, 0% 100%)"
        opacity={0}
        animation={`${speakWave1} 3s ease-out backwards infinite`}
        zIndex={1}
        pointerEvents="none"
      />

      {/* Arc 2 - Dark Gray */}
      <Box
        position="absolute"
        bottom="2px"
        left="0%"
        width="33px"
        height="21px"
        borderRadius="100%"
        border="2.5px solid"
        borderColor="purple.700"
        borderTop="transparent"
        borderLeft="transparent"
        borderRight="transparent"
        transform="translateX(-50%) rotate(-30deg)"
        transformOrigin="center top"
        clipPath="polygon(0% 85%, 100% 85%, 100% 100%, 0% 100%)"
        opacity={0}
        animation={`${speakWave1} 3s ease-out backwards infinite 0.6s`}
        zIndex={1}
        pointerEvents="none"
      />

      {/* Arc 3 - Black */}
      <Box
        position="absolute"
        bottom="2px"
        left="0%"
        width="33px"
        height="21px"
        borderRadius="100%"
        border="2px solid"
        borderColor="purple.700"
        borderTop="transparent"
        borderLeft="transparent"
        borderRight="transparent"
        transform="translateX(-50%) rotate(-30deg)"
        transformOrigin="center top"
        clipPath="polygon(0% 85%, 100% 85%, 100% 100%, 0% 100%)"
        opacity={0}
        animation={`${speakWave1} 3s ease-out backwards infinite 1.2s`}
        zIndex={1}
        pointerEvents="none"
      />
    </Box>
  );
};

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType;
  iconBoxSize?: string | number;
  onClick: () => void;
}

interface CollapsibleLeftNavProps {
  title: string;
  titleIcon?: React.ComponentType;
  items: NavItem[];
  activeItemId: string;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  // EVA session list props
  isFromEvaPlayground?: boolean;
  sessions?: DarwinHeader[];
  selectedSessionId?: string;
  onSessionSelect?: (sessionId: string) => void;
  onSessionDelete?: (sessionId: string) => void;
  onSessionsRefresh?: () => Promise<void>;
  isLoadingSessions?: boolean;
}

const CollapsibleLeftNav: React.FC<CollapsibleLeftNavProps> = ({
  title,
  titleIcon,
  items,
  activeItemId,
  defaultWidth = 200,
  minWidth = 180,
  maxWidth = 350,
  // EVA session list props
  isFromEvaPlayground = false,
  sessions = [],
  selectedSessionId = "",
  onSessionSelect,
  onSessionDelete,
  onSessionsRefresh,
  isLoadingSessions = false,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [width, setWidth] = useState(defaultWidth);
  const [isResizing, setIsResizing] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);

  // Edit modal state
  const { isOpen: isEditModalOpen, onOpen: onEditModalOpen, onClose: onEditModalClose } = useDisclosure();
  const [editingSession, setEditingSession] = useState<DarwinHeader | null>(null);

  // Open edit modal for a session
  const handleEditClick = (e: React.MouseEvent, session: DarwinHeader) => {
    e.stopPropagation();
    setEditingSession(session);
    onEditModalOpen();
  };

  // Handle modal close
  const handleModalClose = () => {
    onEditModalClose();
    setEditingSession(null);
  };

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, [width]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;

    const deltaX = e.clientX - startXRef.current;
    const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidthRef.current + deltaX));
    setWidth(newWidth);
  }, [isResizing, minWidth, maxWidth]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, []);

  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <Box
      ref={navRef}
      bg="bg.surface"
      boxShadow="lg"
      borderRadius="lg"
      overflowY="hidden"
      width={isCollapsed ? "60px" : `${width}px`}
      minWidth={isCollapsed ? "60px" : `${minWidth}px`}
      maxWidth={isCollapsed ? "60px" : `${maxWidth}px`}
      transition={isResizing ? "none" : "width 0.3s ease"}
      position="relative"
    >
      <Box
        borderColor="#4e1e6c"
        height="100%"
        display="flex"
        flexDirection="column"
      >
        <Box bg="bg.surface" p={2} height="100%">
          {/* Header with title and collapse button */}
          <Box display="flex" alignItems="center" justifyContent="space-between">
            {!isCollapsed && (
              <HStack flex="1" spacing={2}>
                {titleIcon && <SpeakingIconWrapper icon={titleIcon} boxSize={8} />}
                <Text
                  fontWeight="bold"
                  color="fg.default"
                  fontSize="lg"
                  mb={0}
                >
                  {title}
                </Text>
              </HStack>
            )}
            <IconButton
              aria-label={isCollapsed ? "Expand navigation" : "Collapse navigation"}
              icon={isCollapsed ? <Icon as={TbLayoutSidebarLeftExpandFilled} boxSize={7} color="gray.600" /> :
              <Icon as={TbLayoutSidebarRightExpandFilled} boxSize={7} color="gray.500" />}
              size="sm"
              variant="ghost"
              onClick={toggleCollapse}
              _hover={{ bg: "gray.100" }}
            />
          </Box>

           <Divider mb={3} mt={2} ></Divider>

          <Stack
            height={isFromEvaPlayground && !isCollapsed ? "auto" : "calc(100% - 60px)"}
            spacing="1"
            alignItems="left"
            overflowY={isFromEvaPlayground ? "visible" : "auto"}
            sx={{
              "::-webkit-scrollbar": { width: "4px" },
              "::-webkit-scrollbar-track": { background: "gray.100" },
              "::-webkit-scrollbar-thumb": {
                background: "gray.500",
                borderRadius: "8px"
              },
              "::-webkit-scrollbar-thumb:hover": { background: "gray.600" },
              scrollbarWidth: "thin",
              scrollbarColor: "gray.500 gray.100",
            }}
          >
            {items.map((item) => (
              <Button
                key={item.id}
                variant="tertiary"
                justifyContent={isCollapsed ? "center" : "start"}
                iconSpacing="3"
                leftIcon={!isCollapsed ? (
                  <Box width={6} display="flex" alignItems="center" justifyContent="center">
                    <Icon as={item.icon} boxSize={item.iconBoxSize || "1.2rem"} />
                  </Box>
                ) : undefined}
                _hover={{ bg: activeItemId === item.id ? "purple.600" : "purple.50"}}
                bg={activeItemId === item.id ? "purple.700" : "white"}
                color={activeItemId === item.id ? "white" : "fg.default"}
                borderRadius="lg"
                py={2}
                onClick={item.onClick}
                cursor="pointer"
                title={isCollapsed ? item.label : undefined}
              >
                {isCollapsed ? (
                  <Icon as={item.icon} boxSize={item.iconBoxSize || "1.2rem"} minWidth={6} />
                ) : (
                  <Text fontSize="sm" fontWeight={activeItemId === item.id ? "600" : "600"} align="left" m={0}>
                    {item.label}
                  </Text>
                )}
              </Button>
            ))}
          </Stack>

          {/* EVA Sessions List - Only when isFromEvaPlayground and not collapsed */}
          {isFromEvaPlayground && !isCollapsed && (
            <>
              <Divider my={4} />
              <Box flex={1} overflow="hidden" display="flex" flexDirection="column" maxH="100%">
              <HStack spacing={2} mb={4} px={2}>
              <Icon as={IoChatbubbleOutline} boxSize={5} color="fg.default" />
                <Text fontSize="sm" color="fg.default" mb={0} fontWeight="600" textAlign="left">
                  EVA Chat Sessions
                </Text>
                </HStack>

                {isLoadingSessions ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <Spinner size="sm" color="purple.500" />
                  </Box>
                ) : sessions.length === 0 ? (
                  <Text fontSize="xs" color="gray.400" textAlign="center" py={4}>
                    No saved chats yet
                  </Text>
                ) : (
                  <Box
                    flex={1}
                    overflowY="auto"
                    pb={2}
                    pr={1}
                    css={{
                      "&::-webkit-scrollbar": {
                        width: "4px",
                      },
                      "&::-webkit-scrollbar-track": {
                        background: "transparent",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: "#dfc2f0",
                        borderRadius: "2px",
                      },
                      "&::-webkit-scrollbar-thumb:hover": { background: "gray.600" },
                      scrollbarWidth: "thin",
                    }}
                  >
                    {sessions.map((session) => {
                      const isSelected = selectedSessionId === session.id;
                      return (
                        <HStack
                          key={session.id}
                          p={2}
                          bg={isSelected ? "purple.50" : "transparent"}
                          borderRadius="md"
                          _hover={{ bg: isSelected ? "purple.100" : "gray.100" }}
                          mb={1}
                          spacing={2}
                          cursor="pointer"
                          onClick={() => onSessionSelect?.(session.id!)}
                          role="group"
                        >
                          <Text
                            flex={1}
                            fontSize="sm"
                            textDecoration="italic"
                            fontWeight={isSelected ? "700" : "500"}
                            color="gray.700"
                            noOfLines={1}
                            m={0}
                            textAlign="left"
                          >
                            {session.name || "Untitled"}
                          </Text>
                          <Menu placement="right-start">
                            <MenuButton
                              as={IconButton}
                              icon={<Icon as={FiMoreVertical} boxSize={3} />}
                              size="xs"
                              variant="ghost"
                              color="gray.400"
                              _hover={{ color: "purple.500", bg: "purple.50" }}
                              aria-label="Chat options"
                              minW="auto"
                              h="auto"
                              p={1}
                              onClick={(e) => e.stopPropagation()}
                              opacity={0}
                              _groupHover={{ opacity: 1 }}
                              transition="opacity 0.2s"
                            />
                            <MenuList minW="120px" zIndex={1001}>
                              <MenuItem
                                icon={<Icon as={FiEdit2} boxSize={3} />}
                                fontSize="xs"
                                onClick={(e) => handleEditClick(e, session)}
                              >
                                Edit
                              </MenuItem>
                              <MenuItem
                                icon={<Icon as={FiTrash2} boxSize={3} color="red.500" />}
                                fontSize="xs"
                                color="red.500"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSessionDelete?.(session.id!);
                                }}
                              >
                                Delete
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        </HStack>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Resize handle */}
      {!isCollapsed && (
        <Box
          position="absolute"
          right={0}
          top={0}
          bottom={0}
          width="4px"
          bg="transparent"
          cursor="col-resize"
          _hover={{ bg: "purple.200" }}
          onMouseDown={handleMouseDown}
        />
      )}

      {/* Edit Chat Modal */}
      <EditChatModal
        isOpen={isEditModalOpen}
        onClose={handleModalClose}
        sessionId={editingSession?.id || null}
        initialTitle={editingSession?.name}
        initialDescription={editingSession?.description}
        onSuccess={async () => {
          if (onSessionsRefresh) {
            await onSessionsRefresh();
          }
        }}
      />
    </Box>
  );
};

export default CollapsibleLeftNav;
